import { queryOptions, useQuery, type UseQueryResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { MAX_PAGE_SIZE, type PageWire } from "@/lib/pagination"
import { qk } from "@/lib/query-keys"
import { Role } from "@/lib/roles"

import type { ClientUserOption, UserId } from "../clients-data"

export interface UserOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
  is_active: boolean
  created_at: string
  invited_at: string | null
  accepted_at: string | null
}

export function toWireUserIdOrNull(userId: UserId | null): number | null {
  return userId === null ? null : Number(userId)
}

function toClientUserOption(u: UserOutWire): ClientUserOption {
  return {
    id: String(u.id) as UserId,
    name: u.full_name ?? u.email,
    email: u.email,
  }
}

function compareUsersByName(firstUser: UserOutWire, secondUser: UserOutWire): number {
  return (firstUser.full_name ?? firstUser.email).localeCompare(
    secondUser.full_name ?? secondUser.email,
  )
}

export function toActiveUserOptionsByName(users: UserOutWire[]): ClientUserOption[] {
  return users
    .filter((user) => user.is_active)
    .sort(compareUsersByName)
    .map(toClientUserOption)
}

export function toUsersById(users: UserOutWire[]): Map<number, UserOutWire> {
  return new Map(users.map((user) => [user.id, user]))
}

async function fetchEveryUserWithRoles(
  roles: readonly Role[],
  signal: AbortSignal,
): Promise<UserOutWire[]> {
  const fetchUserPage = (page: number) =>
    http.get<PageWire<UserOutWire>>("/api/v1/users", {
      params: { role: [...roles], sort: "createdAt", dir: "asc", page, page_size: MAX_PAGE_SIZE },
      signal,
    })

  const firstUserPage = await fetchUserPage(1)
  const userPageCount = Math.ceil(firstUserPage.total / MAX_PAGE_SIZE)
  const remainingPageNumbers = Array.from(
    { length: Math.max(userPageCount - 1, 0) },
    (_, remainingPageIndex) => remainingPageIndex + 2,
  )
  const remainingUserPages = await Promise.all(remainingPageNumbers.map(fetchUserPage))

  const everyUser = [firstUserPage, ...remainingUserPages].flatMap((userPage) => userPage.items)
  return [...toUsersById(everyUser).values()]
}

export const usersWithRolesQuery = (roles: readonly Role[]) =>
  queryOptions({
    queryKey: qk.users.list({ role: roles }),
    queryFn: ({ signal }) => fetchEveryUserWithRoles(roles, signal),
    staleTime: 60_000,
  })

// ── The client-role user roster (small, static-ish). ──────────────────────
//
// One fetch of the raw wire, two derived shapes via `select` so the "add member"
// combobox (sorted ClientUserOption[]) and the row adapter (Map<id, UserOut>) don't
// each cost a round trip.

const CLIENT_CONTACT_ROLES: readonly Role[] = [Role.CLIENT]

export const clientUsersRawQuery = () => usersWithRolesQuery(CLIENT_CONTACT_ROLES)

export const clientUsersQuery = () =>
  queryOptions({
    ...clientUsersRawQuery(),
    select: toActiveUserOptionsByName,
  })

export function useClientUsersQuery(): UseQueryResult<ClientUserOption[]> {
  return useQuery(clientUsersQuery())
}
